import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { SharedTable } from '../common';

const TasksTableWithShared = ({
  tasks,
  selectedTasks,
  setSelectedTasks,
  selectAll,
  setSelectAll,
  handleSelectAll,
  handleEditTask,
  handleDeleteTask,
  handleStatusChange,
  taskTransitions,
  users,
  pagination,
  onPageChange,
  onLimitChange, // Add onLimitChange prop here
  sortBy,
  sortOrder,
  handleSort,
  showColumnModal,
  setShowColumnModal,
  loading = false,
  handleUpdateTaskDueDate // New prop for updating due date directly
}) => {
  // Column configuration with default order and width
  const defaultColumns = [
    { id: 'status', label: 'Status', visible: true, width: 80 },
    { id: 'title', label: 'Task', visible: true, width: 200 },
    { id: 'due_at', label: 'Due Date/Time', visible: true, width: 150, sortable: true },
    { id: 'priority', label: 'Priority', visible: true, width: 100 },
    { id: 'type', label: 'Type', visible: true, width: 100 },
    { id: 'assigned_to', label: 'Assigned To', visible: true, width: 150 },
    { id: 'lead', label: 'Related Lead', visible: true, width: 150 },
    { id: 'actions', label: 'Actions', visible: true, width: 120 }
  ];

  // Load column order from localStorage or use default
  const [columns, setColumns] = useState(() => {
    const savedColumns = localStorage.getItem('tasksTableColumns');
    return savedColumns ? JSON.parse(savedColumns) : defaultColumns;
  });
  
  // Keep a reference to default columns for reset functionality
  const defaultColumnsRef = useRef(defaultColumns);

  // Save column configuration to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('tasksTableColumns', JSON.stringify(columns));
  }, [columns]);
  
  // Handle reset to default
  const handleResetColumns = () => {
    setColumns([...defaultColumnsRef.current]);
  };

  // Handle individual task selection
  const handleTaskSelection = (taskId) => {
    if (selectedTasks.includes(taskId)) {
      setSelectedTasks(selectedTasks.filter(id => id !== taskId));
      setSelectAll(false);
    } else {
      setSelectedTasks([...selectedTasks, taskId]);
      if (selectedTasks.length + 1 === tasks.length) {
        setSelectAll(true);
      }
    }
  };

  // Helper function to get priority badge color
  const getPriorityBadgeColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Helper function to get status text
  const getStatusText = (status) => {
    switch (status) {
      case 'pending':
        return 'Pending';
      case 'in_progress':
        return 'Pending';
      case 'completed':
        return 'Completed';
      default:
        return status;
    }
  };

  // Helper function to get type badge color and text
  const getTypeBadge = (type) => {
    switch (type) {
      case 'support':
        return {
          color: 'bg-purple-100 text-purple-800',
          text: 'Support'
        };
      case 'task':
      default:
        return {
          color: 'bg-blue-100 text-blue-800',
          text: 'Sales'
        };
    }
  };

  // State to track which task is being edited
  const [editingDueDate, setEditingDueDate] = useState(null);
  const [dueDate, setDueDate] = useState('');
  const [dueTime, setDueTime] = useState('');

  // Handle editing due date
  const handleDueDateClick = (task, e) => {
    // Don't proceed if handleUpdateTaskDueDate is not provided
    if (!handleUpdateTaskDueDate) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    // Parse the task's due date for the form
    if (task.due_at) {
      const dueDateTime = new Date(task.due_at);
      
      // Format date as YYYY-MM-DD for date input
      const year = dueDateTime.getFullYear();
      const month = (dueDateTime.getMonth() + 1).toString().padStart(2, '0');
      const day = dueDateTime.getDate().toString().padStart(2, '0');
      setDueDate(`${year}-${month}-${day}`);
      
      // Format time as HH:MM for time input
      const hours = dueDateTime.getHours().toString().padStart(2, '0');
      const minutes = dueDateTime.getMinutes().toString().padStart(2, '0');
      setDueTime(`${hours}:${minutes}`);
    } else {
      // Default to today if no existing due date
      const today = new Date();
      const year = today.getFullYear();
      const month = (today.getMonth() + 1).toString().padStart(2, '0');
      const day = today.getDate().toString().padStart(2, '0');
      setDueDate(`${year}-${month}-${day}`);
      setDueTime('');
    }
    
    setEditingDueDate(task.id);
  };
  
  // Save due date changes
  const saveDueDate = (taskId) => {
    if (handleUpdateTaskDueDate && dueDate) {
      handleUpdateTaskDueDate(taskId, dueDate, dueTime);
      setEditingDueDate(null);
    }
  };
  
  // Cancel due date editing
  const cancelDueDateEdit = () => {
    setEditingDueDate(null);
  };

  // Handle clicks outside the date editor
  useEffect(() => {
    const handleClickOutside = (e) => {
      // If clicking outside and we're editing, cancel the edit
      if (editingDueDate && 
          !e.target.closest('.due-date-editor') && 
          !e.target.closest('.due-date-display')) {
        cancelDueDateEdit();
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [editingDueDate]);

  // Render cell content based on column ID
  const renderCell = (task, columnId) => {
    switch (columnId) {
      case 'status':
        return (
          <div className="flex items-center justify-center">
            {taskTransitions[task.id] ? (
              // Show spinner during transition
              <svg className="animate-spin h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : (
              <button
                onClick={() => handleStatusChange(
                  task.id, 
                  task.status === 'completed' ? 'pending' : 'completed'
                )}
                className={`h-6 w-6 flex items-center justify-center rounded-full border-2 focus:outline-none transition-colors ${
                  task.status === 'completed' 
                    ? 'bg-green-500 border-green-500 hover:bg-green-600 hover:border-green-600' 
                    : 'border-gray-300 hover:border-blue-500'
                }`}
                aria-label={task.status === 'completed' ? "Mark as incomplete" : "Mark as complete"}
              >
                {task.status === 'completed' && (
                  <svg className="h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </button>
            )}
          </div>
        );
      case 'title':
        return (
          <div>
            <div className={`text-sm font-medium ${task.status === 'completed' ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
              {task.title}
            </div>
            <div className="text-sm text-gray-500">{task.description}</div>
          </div>
        );
      case 'due_at':
        // Check if we're editing this task's due date
        if (editingDueDate === task.id) {
          return (
            <div className="due-date-editor">
              <div className="flex flex-col gap-1">
                <div className="flex gap-1">
                  <input
                    type="date"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    className="w-full text-xs border border-blue-300 rounded p-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div className="flex gap-1">
                  <input
                    type="time"
                    value={dueTime}
                    onChange={(e) => setDueTime(e.target.value)}
                    className="w-full text-xs border border-blue-300 rounded p-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div className="flex justify-between mt-1">
                  <button 
                    className="text-xs text-white bg-blue-500 hover:bg-blue-600 rounded px-2 py-1" 
                    onClick={() => saveDueDate(task.id)}
                  >
                    Save
                  </button>
                  <button 
                    className="text-xs text-gray-700 bg-gray-200 hover:bg-gray-300 rounded px-2 py-1" 
                    onClick={cancelDueDateEdit}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          );
        }
        
        return (
          <div 
            className="due-date-display cursor-pointer hover:bg-gray-100 p-1 rounded"
            onClick={(e) => handleDueDateClick(task, e)}
          >
            {task.due_at && (
              <div className={`text-sm ${new Date(task.due_at) < new Date() && task.status !== 'completed' ? 'text-red-600 font-semibold' : 'text-gray-900'}`}>
                {new Date(task.due_at).toLocaleDateString()}
                <div className="text-xs text-gray-600">
                  {new Date(task.due_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            )}
          </div>
        );
      case 'priority':
        return (
          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getPriorityBadgeColor(task.priority)}`}>
            {task.priority.charAt(0).toUpperCase() + task.priority.slice(1)}
          </span>
        );
      case 'type':
        return (
          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getTypeBadge(task.type).color}`}>
            {getTypeBadge(task.type).text}
          </span>
        );
      case 'assigned_to':
        // For backward compatibility, handle both ID and name formats
        // If task.assigned_to is already a string (name), use it directly
        // Otherwise, try to find the user by ID and get their name
        return (
          <div className="text-sm text-gray-900">
            {typeof task.assigned_to === 'string' 
              ? task.assigned_to || 'Unassigned' 
              : (users.find(user => user.id === task.assigned_to)?.name || 'Unassigned')}
          </div>
        );
      case 'lead':
        return (
          <div>
            {task.lead ? (
              <div className="text-sm text-gray-900 flex items-center">
                {task.lead.phone && (
                  <a 
                    href={`tel:${task.lead.phone}`} 
                    className="text-blue-500 hover:text-blue-700 mr-2" 
                    title="Call this number (will increment call attempts)"
                    onClick={async (e) => {
                      // Don't prevent default so the tel: link still works
                      try {
                        // Increment call attempts via API
                        const token = localStorage.getItem('token');
                        await axios.patch(
                          `/api/leads/${task.lead.id}/call-attempts`,
                          { increment: true },
                          {
                            headers: {
                              'Content-Type': 'application/json',
                              'x-auth-token': token
                            }
                          }
                        );
                      } catch (err) {
                        console.error('Error updating call attempts:', err);
                      }
                    }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                    </svg>
                  </a>
                )}
                <button 
                  onClick={(e) => {
                    e.preventDefault();
                    if (typeof window.showLeadDetailsModal === 'function') {
                      window.showLeadDetailsModal(task.lead.id);
                    }
                  }} 
                  className="text-blue-600 hover:text-blue-800 cursor-pointer"
                >
                  {task.lead.name}
                </button>
              </div>
            ) : (
              <div className="text-sm text-gray-500">None</div>
            )}
          </div>
        );
      case 'actions':
        return (
          <div className="flex space-x-3">
            <button 
              onClick={() => handleEditTask(task)} 
              className="text-blue-600 hover:text-blue-900"
            >
              Edit
            </button>
            <button 
              onClick={() => handleDeleteTask(task.id)} 
              className="text-red-600 hover:text-red-900"
            >
              Delete
            </button>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <SharedTable
      data={tasks}
      columns={columns}
      selectedItems={selectedTasks}
      onSelectItem={handleTaskSelection}
      selectAll={selectAll}
      onSelectAll={handleSelectAll}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSort={handleSort}
      renderCell={renderCell}
      loading={loading}
      emptyMessage="No tasks found. Create your first task to get started."
      resizable={true}
      onColumnsChange={setColumns}
      showColumnManagement={showColumnModal}
      onToggleColumnManagement={setShowColumnModal}
      pagination={pagination}
      onPageChange={onPageChange}
      onLimitChange={onLimitChange} // Pass onLimitChange down to SharedTable
      onResetColumns={handleResetColumns}
      tableHeight="calc(100vh - 220px)" // Match the height used in leads view to reduce unused space
    />
  );
};

export default TasksTableWithShared;
